import clsx from 'clsx'
import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import PencilChecklistPersonImageSrc from '../../images/pencil-checklist-person.png'
import { ComputeFactory } from '../../models/ComputeFactory'
import { Gift } from '../../models/Gift'
import { Package } from '../../models/Package'
import { Source } from '../../models/Source'
import { Transaction } from '../../models/Transaction'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { Icon, IconNameAndCategoryString } from '../Icon'
import Button from '../app/design/Button'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { SelectModal } from '../common/SelectModal'
import { CurrencyField } from '../common/fields/CurrencyField'
import { TextField } from '../common/fields/TextField'
import { DiscountModal } from './DiscountModal'
import { RemovableItemContainer } from './RemovableItemContainer'
import { SalesTaxModal } from './SalesTaxModal'
import { WorkflowIconSection } from './WorkflowIconSection'

export type Receipt = {
    items?: {
        id: string
        title: string
        subtitle?: string
        thirdLine?: string
        tertiaryView?: ReactNode
        quantity: number
        price: number
    }[]
    modifiers?: {
        deleteButtonMargin?: string
        extraRows?: {
            title: string
            icon: IconNameAndCategoryString
            amount: number
        }[]
        subtotal?: {
            amount: number
            title?: string
            subtitle?: string
            onUpdate?: (newAmount: number) => void
        }
        package?: Package
        giftCertificate?: {
            selectedGift?: Gift
            onSelectGift?: (newGift: Gift | undefined) => void
        }
        discount?: {
            discountInstances?: {
                selectedID: string
                onUpdateID: (newID: string) => void
            }
            onChangeDiscount?: () => void
        }
        salesTax?: {
            amount: number | null
            parenthetical: string | null
        }
        surcharge?: {
            amount: number | null
            onAdd?: () => void
            onRemove?: () => void
        }
        shipping?: {
            amount: number | null
            onAdd?: () => void
            onRemove?: () => void
        }
        setupFee?: {
            amount: number | null
            onChange?: (newAmount: number | null) => void
        }
        deposit?: {
            amount: number | null
            title?: string
            subtitle?: string
            isPaid?: boolean
            onChange?: (newAmount: number | null) => void
        }
        gratuity?: {
            amount: number | undefined
            percent: number | undefined
        }
    }
    total?: number
    markedPaidSourceDescription?: string
    paymentMethodSource?: Source
    max?: number
    totalText?: string
}

type Props<TransactionT extends Transaction & { gift?: Gift }> = {
    receipt: Receipt
    transaction: TransactionT
    onUpdateTransaction?: (updatedTransaction: TransactionT) => void
    owner?: User
    client?: User | null
    modifierGapSize?: 'small' | 'medium'
    showModifierDivider?: boolean
    dividerClassName?: string
    totalDividerClassName?: string
}

export const ReceiptView = <TransactionT extends Transaction & { gift?: Gift }>({
    receipt,
    transaction,
    onUpdateTransaction,
    owner,
    client,
    modifierGapSize = 'medium',
    showModifierDivider = true,
    dividerClassName,
    totalDividerClassName,
}: Props<TransactionT>) => {
    const [isSetupFeeModalOpen, setIsSetupFeeModalOpen] = useState(false)
    const [isDepositModalOpen, setIsDepositModalOpen] = useState(false)
    const [isGiftsSelectModalOpen, setIsGiftsSelectModalOpen] = useState(false)
    const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false)
    const [isSalesTaxModalOpen, setIsSalesTaxModalOpen] = useState(false)

    const [gifts, setGifts] = useState<Gift[]>([])

    const giftItems = useMemo(() => {
        return gifts.map((gift) => ({
            id: gift.id ?? '',
            tertiaryView: <Icon icon="gift.large" background="round" />,
            title: gift.item?.name ?? '',
            subtitle:
                gift.balance !== undefined
                    ? `${currencyFormat(gift.balance)} remaining balance`
                    : undefined,
            gift,
        }))
    }, [gifts])
    const selectedGiftItem = (giftItems ?? []).find(
        (item) => item.gift.id === receipt.modifiers?.giftCertificate?.selectedGift?.id
    )
    const handleSelectGiftItems = (giftItems: { gift: Gift }[]) => {
        setIsGiftsSelectModalOpen(false)
        receipt.modifiers?.giftCertificate?.onSelectGift?.(giftItems[0]?.gift)
    }

    useEffect(() => {
        if (!client || !owner) {
            setGifts([])
            return
        }
        ;(async () => {
            setGifts(await ComputeFactory.getInstance().fetchPaidGifts(client, owner))
        })()
    }, [client, owner])

    const hasExceededMax = Boolean(receipt.max) && (receipt.total ?? 0) > (receipt.max ?? 0)
    const hasPrepaidPackage = Boolean(receipt.modifiers?.package)

    const [packageDisplayString, setPackageDisplayString] = useState('')

    useEffect(() => {
        ;(async () => {
            if (!receipt.modifiers?.package) return

            const available = await receipt.modifiers.package.available()
            const { capacityType, capacity, name } = receipt.modifiers.package

            const remainingDisplayString = Package.capacityDisplayString(
                capacityType ?? 'session',
                available ?? capacity ?? 0
            )

            setPackageDisplayString(`${name} · ${remainingDisplayString} remaining`)
        })()
    }, [receipt.modifiers?.package])

    return (
        <>
            <div>
                {receipt.items !== undefined && (
                    <div className="grid grid-cols-[1fr_min-content_min-content]">
                        <Label className="py-4 font-normal text-gray-600">Item</Label>
                        <Label className="py-4 pl-10 font-normal text-gray-600">Quantity</Label>
                        <Label className="py-4 pl-10 text-right font-normal text-gray-600">
                            Price
                        </Label>

                        {receipt.items.map((item, itemIndex) => (
                            <Fragment key={item.id}>
                                <div className="border-t border-gray-200 py-4">
                                    <div
                                        className={twm(
                                            'flex justify-start gap-x-4',
                                            item.thirdLine === undefined
                                                ? 'items-center'
                                                : 'items-start'
                                        )}
                                    >
                                        {item.tertiaryView !== undefined && (
                                            <div className="shrink-0">{item.tertiaryView}</div>
                                        )}

                                        <div className="flex flex-col items-stretch justify-center gap-y-1">
                                            <Label>{item.title}</Label>
                                            {item.subtitle !== undefined && (
                                                <Paragraph
                                                    size="sm"
                                                    className="font-normal text-gray-600"
                                                >
                                                    {item.subtitle}
                                                </Paragraph>
                                            )}
                                            {item.thirdLine !== undefined && (
                                                <Paragraph
                                                    size="sm"
                                                    className="font-normal text-gray-600"
                                                >
                                                    {item.thirdLine}
                                                </Paragraph>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <Label className="border-t border-gray-200 py-4 pl-10 text-right font-normal">
                                    {item.quantity}
                                </Label>
                                <Label className="border-t border-gray-200 py-4 pl-10 text-right">
                                    {currencyFormat(item.price, true)}
                                </Label>
                            </Fragment>
                        ))}
                    </div>
                )}

                {receipt.items !== undefined &&
                    (receipt.modifiers !== undefined || receipt.total !== undefined) && (
                        <Divider
                            className={
                                dividerClassName ??
                                (modifierGapSize === 'small' ? 'mt-0 mb-[10px]' : 'mt-0 mb-6')
                            }
                        />
                    )}

                {receipt.modifiers !== undefined && (
                    <div
                        className="flex flex-col items-stretch justify-start pr-1"
                        data-testid="receipt-modifiers"
                    >
                        <div
                            data-testid="receipt-modifiers-inner"
                            className={twm(
                                'flex flex-col items-stretch justify-start',
                                modifierGapSize === 'small' ? 'gap-y-1' : 'gap-y-5'
                            )}
                        >
                            {receipt.modifiers.extraRows !== undefined && (
                                <>
                                    {receipt.modifiers.extraRows.map((row) => (
                                        <ModifierRow
                                            key={row.title}
                                            title={row.title}
                                            icon={row.icon}
                                            amount={row.amount}
                                        />
                                    ))}
                                </>
                            )}

                            {receipt.modifiers.subtotal !== undefined && (
                                <ModifierRow
                                    title={receipt.modifiers.subtotal.title ?? 'Subtotal'}
                                    secondLine={
                                        receipt.modifiers.subtotal.subtitle ? (
                                            <Paragraph size="sm" className="text-gray-600">
                                                {receipt.modifiers.subtotal.subtitle}
                                            </Paragraph>
                                        ) : undefined
                                    }
                                    icon="money2.large"
                                    amount={receipt.modifiers.subtotal.amount}
                                    onUpdateCurrencyAmount={
                                        !hasPrepaidPackage
                                            ? receipt.modifiers.subtotal.onUpdate
                                            : undefined
                                    }
                                    deleteButtonMargin={receipt.modifiers.deleteButtonMargin}
                                />
                            )}

                            {receipt.modifiers.deposit !== undefined && !hasPrepaidPackage && (
                                <ModifierRow
                                    title={
                                        !!receipt.modifiers.deposit.title
                                            ? receipt.modifiers.deposit.title
                                            : 'Deposit'
                                    }
                                    secondLine={
                                        receipt.modifiers.deposit.subtitle ? (
                                            <Paragraph size="sm" className="text-gray-600">
                                                {receipt.modifiers.deposit.subtitle}
                                            </Paragraph>
                                        ) : undefined
                                    }
                                    icon="safe.large"
                                    amount={
                                        receipt.modifiers.deposit.isPaid
                                            ? -1 * (receipt.modifiers.deposit.amount ?? 0)
                                            : receipt.modifiers.deposit.amount
                                    }
                                    onAdd={
                                        receipt.modifiers?.deposit?.onChange !== undefined &&
                                        !receipt.modifiers?.deposit.isPaid
                                            ? () => receipt.modifiers?.deposit?.onChange?.(0)
                                            : undefined
                                    }
                                    onRemove={
                                        receipt.modifiers?.deposit?.onChange !== undefined &&
                                        !receipt.modifiers?.deposit.isPaid
                                            ? () => receipt.modifiers?.deposit?.onChange?.(null)
                                            : undefined
                                    }
                                    onUpdateCurrencyAmount={
                                        receipt.modifiers?.deposit?.amount !== null &&
                                        !receipt.modifiers.deposit.isPaid
                                            ? receipt.modifiers?.deposit?.onChange
                                            : undefined
                                    }
                                    deleteButtonMargin={receipt.modifiers.deleteButtonMargin}
                                />
                            )}
                        </div>

                        {(receipt.modifiers.extraRows !== undefined ||
                            receipt.modifiers.subtotal !== undefined ||
                            receipt.modifiers.deposit !== undefined) &&
                            (receipt.modifiers.setupFee !== undefined ||
                                receipt.modifiers.package !== undefined ||
                                receipt.modifiers.giftCertificate !== undefined ||
                                receipt.modifiers.discount ||
                                receipt.modifiers.salesTax !== undefined ||
                                receipt.modifiers.surcharge !== undefined ||
                                receipt.modifiers.shipping !== undefined) &&
                            showModifierDivider && (
                                <Divider
                                    className={
                                        dividerClassName ??
                                        (modifierGapSize === 'small'
                                            ? 'mt-[10px] mb-2'
                                            : 'my-[18px]')
                                    }
                                />
                            )}

                        <div
                            className={twm(
                                'flex flex-col items-stretch justify-start',
                                modifierGapSize === 'small' ? 'gap-y-1' : 'gap-y-[10px]'
                            )}
                        >
                            {receipt.modifiers.setupFee !== undefined && (
                                <ModifierRow
                                    title="Setup fee"
                                    icon="coins2.large"
                                    amount={receipt.modifiers.setupFee.amount}
                                    onUpdateCurrencyAmount={
                                        receipt.modifiers.setupFee.amount === null
                                            ? undefined
                                            : receipt.modifiers?.setupFee?.onChange
                                    }
                                    onAdd={
                                        receipt.modifiers?.setupFee?.onChange !== undefined
                                            ? () => receipt.modifiers?.setupFee?.onChange?.(0)
                                            : undefined
                                    }
                                    onRemove={
                                        receipt.modifiers?.setupFee?.onChange !== undefined
                                            ? () => receipt.modifiers?.setupFee?.onChange?.(null)
                                            : undefined
                                    }
                                    deleteButtonMargin={receipt.modifiers.deleteButtonMargin}
                                />
                            )}

                            {receipt.modifiers.package !== undefined && (
                                <ModifierRow
                                    title="Package"
                                    icon="package2.large"
                                    secondLine={
                                        receipt.modifiers.package ? (
                                            <Paragraph size="sm" className="text-gray-600">
                                                {packageDisplayString}
                                            </Paragraph>
                                        ) : undefined
                                    }
                                    amount={
                                        transaction.discount !== undefined
                                            ? -1 * (transaction.discountAmount ?? 0)
                                            : null
                                    }
                                    deleteButtonMargin={receipt.modifiers.deleteButtonMargin}
                                />
                            )}

                            {receipt.modifiers.giftCertificate !== undefined &&
                                !hasPrepaidPackage && (
                                    <>
                                        <ModifierRow
                                            title="Gift certificate"
                                            icon="gift.large"
                                            amount={transaction.gift !== undefined ? 0 : null}
                                            isAmountHidden
                                            onAdd={
                                                onUpdateTransaction !== undefined
                                                    ? () => setIsGiftsSelectModalOpen(true)
                                                    : undefined
                                            }
                                            onEdit={
                                                onUpdateTransaction !== undefined
                                                    ? () => setIsGiftsSelectModalOpen(true)
                                                    : undefined
                                            }
                                            onRemove={
                                                receipt.modifiers?.giftCertificate.onSelectGift !==
                                                undefined
                                                    ? () =>
                                                          receipt.modifiers?.giftCertificate?.onSelectGift?.(
                                                              undefined
                                                          )
                                                    : undefined
                                            }
                                            deleteButtonMargin={
                                                receipt.modifiers.deleteButtonMargin
                                            }
                                            isDisabled={
                                                transaction.gift === undefined && gifts.length === 0
                                            }
                                            secondLine={
                                                receipt.modifiers?.giftCertificate?.selectedGift !==
                                                undefined ? (
                                                    <Paragraph size="sm" className="text-gray-600">
                                                        {
                                                            receipt.modifiers.giftCertificate
                                                                .selectedGift.item?.name
                                                        }{' '}
                                                        {receipt.modifiers.giftCertificate
                                                            .selectedGift.item?.name !==
                                                            undefined && <>&middot;</>}{' '}
                                                        {currencyFormat(
                                                            receipt.modifiers.giftCertificate
                                                                .selectedGift.balance ?? 0
                                                        )}{' '}
                                                        remaining balance
                                                    </Paragraph>
                                                ) : undefined
                                            }
                                        />

                                        <Divider
                                            className={
                                                dividerClassName ??
                                                (modifierGapSize === 'small'
                                                    ? 'mt-[4px] mb-2'
                                                    : 'my-[6px]')
                                            }
                                        />
                                    </>
                                )}

                            {receipt.modifiers.discount !== undefined && !hasPrepaidPackage && (
                                <ModifierRow
                                    title={`Discount ${transaction.gift !== undefined ? '(Gift certificate)' : transaction.discountType === 'rate' ? '(Fixed)' : ''}`}
                                    icon="discount.large"
                                    amount={
                                        transaction.discount !== undefined
                                            ? -1 * (transaction.discountAmount ?? 0)
                                            : null
                                    }
                                    parenthetical={
                                        transaction.discountType === 'percent'
                                            ? `${transaction.discount}%`
                                            : undefined
                                    }
                                    onAdd={
                                        onUpdateTransaction !== undefined
                                            ? () => setIsDiscountModalOpen(true)
                                            : undefined
                                    }
                                    onEdit={
                                        onUpdateTransaction !== undefined
                                            ? () => setIsDiscountModalOpen(true)
                                            : undefined
                                    }
                                    onRemove={
                                        onUpdateTransaction !== undefined
                                            ? () => {
                                                  receipt.modifiers?.discount?.onChangeDiscount?.()
                                                  onUpdateTransaction?.(
                                                      transaction.clone({
                                                          discount: undefined,
                                                          discount_type: undefined,
                                                      }) as TransactionT
                                                  )
                                              }
                                            : undefined
                                    }
                                    deleteButtonMargin={receipt.modifiers.deleteButtonMargin}
                                />
                            )}

                            {receipt.modifiers.salesTax && (
                                <ModifierRow
                                    title="Sales tax"
                                    icon="percentage2.large"
                                    amount={
                                        receipt.modifiers.salesTax.amount !== null
                                            ? receipt.modifiers.salesTax.amount
                                            : transaction.taxcode !== undefined
                                              ? (transaction.taxAmount ?? null)
                                              : null
                                    }
                                    parenthetical={
                                        transaction.taxcode !== undefined
                                            ? `${transaction.taxcode.rate ?? 0}%`
                                            : undefined
                                    }
                                    onAdd={
                                        onUpdateTransaction !== undefined
                                            ? () => setIsSalesTaxModalOpen(true)
                                            : undefined
                                    }
                                    onEdit={
                                        onUpdateTransaction !== undefined
                                            ? () => setIsSalesTaxModalOpen(true)
                                            : undefined
                                    }
                                    onRemove={
                                        onUpdateTransaction !== undefined
                                            ? () =>
                                                  onUpdateTransaction?.(
                                                      transaction.clone({
                                                          taxcode: undefined,
                                                          nexus: undefined,
                                                      }) as TransactionT
                                                  )
                                            : undefined
                                    }
                                    deleteButtonMargin={receipt.modifiers.deleteButtonMargin}
                                />
                            )}

                            {receipt.modifiers.surcharge !== undefined && (
                                <ModifierRow
                                    title="Surcharge"
                                    icon="percentage3.large"
                                    amount={receipt.modifiers.surcharge.amount}
                                    parenthetical={
                                        receipt.modifiers.surcharge.amount !== null
                                            ? '3%'
                                            : undefined
                                    }
                                    onAdd={receipt.modifiers.surcharge.onAdd}
                                    onRemove={receipt.modifiers.surcharge.onRemove}
                                    deleteButtonMargin={receipt.modifiers.deleteButtonMargin}
                                />
                            )}

                            {receipt.modifiers.shipping !== undefined && (
                                <ModifierRow
                                    title="Shipping fee"
                                    icon="truck.large"
                                    amount={receipt.modifiers.shipping.amount}
                                    onAdd={receipt.modifiers.shipping.onAdd}
                                    onRemove={receipt.modifiers.shipping.onRemove}
                                    deleteButtonMargin={receipt.modifiers.deleteButtonMargin}
                                />
                            )}

                            {receipt.modifiers.gratuity !== undefined && (
                                <ModifierRow
                                    title="Gratuity"
                                    icon="money.large"
                                    amount={receipt.modifiers.gratuity.amount ?? 0}
                                    parenthetical={
                                        receipt.modifiers.gratuity.percent !== undefined
                                            ? `${receipt.modifiers.gratuity.percent}%`
                                            : undefined
                                    }
                                    deleteButtonMargin={receipt.modifiers.deleteButtonMargin}
                                />
                            )}
                        </div>
                    </div>
                )}

                {receipt.modifiers !== undefined && receipt.total !== undefined && (
                    <Divider
                        className={
                            totalDividerClassName ??
                            (modifierGapSize === 'small' ? 'mt-[10px] mb-4' : 'mt-[18px] mb-6')
                        }
                    />
                )}

                {receipt.total !== undefined && (
                    <>
                        <div className="flex items-center justify-center gap-x-4">
                            <Icon icon="dollar.large" className="text-gray-700" />
                            <Label className={clsx('grow')}>
                                {receipt.totalText ??
                                    (transaction.paidDate ? 'Amount charged' : 'Amount to charge')}
                            </Label>
                            <div>
                                <Heading
                                    size="sm"
                                    className={clsx('shrink-0', {
                                        'text-right': hasExceededMax,
                                    })}
                                >
                                    {currencyFormat(receipt.total, true)}
                                </Heading>
                            </div>
                        </div>
                        {hasExceededMax && (
                            <Paragraph
                                size="sm"
                                className="text-danger-400 text-right"
                            >{`Total cannot exceed ${currencyFormat(receipt.max)}`}</Paragraph>
                        )}
                    </>
                )}

                {receipt.total !== undefined &&
                    (receipt.paymentMethodSource !== undefined ||
                        receipt.markedPaidSourceDescription !== undefined) && (
                        <Divider className={modifierGapSize === 'small' ? 'my-4' : 'my-6'} />
                    )}

                {(receipt.markedPaidSourceDescription !== undefined ||
                    receipt.paymentMethodSource !== undefined) && (
                    <div className="flex flex-col items-stretch justify-start gap-y-4">
                        {receipt.markedPaidSourceDescription !== undefined && (
                            <div className="flex items-center justify-center gap-x-4">
                                <Icon icon="cards.large" className="text-gray-500" />
                                <Label className="grow font-normal">Payment method</Label>
                                <div className="flex items-center justify-center gap-x-2">
                                    {receipt.markedPaidSourceDescription}
                                </div>
                            </div>
                        )}

                        {receipt.paymentMethodSource !== undefined && (
                            <div className="flex items-center justify-center gap-x-4">
                                <Icon icon="cards.large" className="text-gray-500" />
                                <Label className="grow font-normal">
                                    {receipt.markedPaidSourceDescription !== undefined
                                        ? 'Card on file'
                                        : 'Payment method'}
                                </Label>
                                <div className="flex items-center justify-center gap-x-2">
                                    {receipt.paymentMethodSource.type === 'checking' ? (
                                        <Icon icon="bank.large" className="text-gray-500" />
                                    ) : (
                                        receipt.paymentMethodSource.icon
                                    )}{' '}
                                    {receipt.paymentMethodSource.displayTitleWithEnding}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <SelectModal
                isOpen={isGiftsSelectModalOpen}
                onClose={() => setIsGiftsSelectModalOpen(false)}
                selectionType="single"
                items={giftItems}
                selectedItems={selectedGiftItem !== undefined ? [selectedGiftItem] : []}
                onSelectItems={handleSelectGiftItems}
                title="Select Gift Certificate"
                emptyView={
                    <div className="flex flex-col items-center justify-center">
                        <img src={PencilChecklistPersonImageSrc} width={100} height={100} />
                        <Heading size="xs" className="mt-4">
                            No gift certificates
                        </Heading>
                        <Label size="md" className="mt-2 font-normal text-gray-600">
                            Nothing here yet
                        </Label>
                    </div>
                }
            />

            {receipt.modifiers?.setupFee !== undefined && (
                <Modal
                    isOpen={isSetupFeeModalOpen}
                    onClose={() => setIsSetupFeeModalOpen(false)}
                    title="Setup fee"
                    footer={
                        <div className="flex justify-end">
                            <Button intent="primary" onClick={() => setIsSetupFeeModalOpen(false)}>
                                Done
                            </Button>
                        </div>
                    }
                >
                    <WorkflowIconSection title="Setup fee" icon="dollar9.large">
                        <TextField
                            value={receipt.modifiers.setupFee.amount ?? ''}
                            onChange={(event) => {
                                if (
                                    event.target.value === '' ||
                                    event.target.value.match(/^\d*\.?\d*$/)
                                ) {
                                    receipt.modifiers?.setupFee?.onChange?.(
                                        parseFloat(event.target.value)
                                    )
                                }
                            }}
                            type="number"
                            step={0.01}
                            size="medium"
                            className="grow"
                            isFullWidth
                            icon="dollar9.small"
                        />
                    </WorkflowIconSection>
                </Modal>
            )}

            {receipt.modifiers?.deposit !== undefined && (
                <Modal
                    isOpen={isDepositModalOpen}
                    onClose={() => setIsDepositModalOpen(false)}
                    title="Deposit"
                    footer={
                        <div className="flex justify-end">
                            <Button intent="primary" onClick={() => setIsDepositModalOpen(false)}>
                                Done
                            </Button>
                        </div>
                    }
                >
                    <WorkflowIconSection title="Deposit" icon="dollar9.large">
                        <TextField
                            value={receipt.modifiers.deposit.amount ?? ''}
                            onChange={(event) => {
                                if (
                                    event.target.value === '' ||
                                    event.target.value.match(/^\d*\.?\d*$/)
                                ) {
                                    receipt.modifiers?.deposit?.onChange?.(
                                        parseFloat(event.target.value)
                                    )
                                }
                            }}
                            type="number"
                            step={0.01}
                            size="medium"
                            className="grow"
                            isFullWidth
                            icon="dollar9.small"
                        />
                    </WorkflowIconSection>
                </Modal>
            )}

            <DiscountModal
                isOpen={isDiscountModalOpen}
                onClose={() => setIsDiscountModalOpen(false)}
                discountType={transaction.discountType === 'rate' ? 'rate' : 'percent'}
                discountAmount={transaction.discount ?? 0}
                onChange={(newType, newAmount) => {
                    receipt.modifiers?.discount?.onChangeDiscount?.()
                    onUpdateTransaction?.(
                        transaction.clone({
                            discount_type: newType,
                            discount: newAmount,
                        }) as TransactionT
                    )
                }}
                discountInstances={receipt.modifiers?.discount?.discountInstances}
            />

            {owner !== undefined && (
                <SalesTaxModal
                    isOpen={isSalesTaxModalOpen}
                    onClose={() => setIsSalesTaxModalOpen(false)}
                    nexus={
                        transaction.nexus === 'pro'
                            ? 'pro'
                            : transaction.nexus === 'transaction'
                              ? 'transaction'
                              : ''
                    }
                    ownerTaxcode={owner.taxcode}
                    taxcode={transaction.taxcode}
                    onChange={(newNexus, newTaxcode) =>
                        onUpdateTransaction?.(
                            transaction.clone({
                                nexus: newNexus,
                                taxcode: newTaxcode,
                                tax: newTaxcode?.rate,
                            }) as TransactionT
                        )
                    }
                />
            )}
        </>
    )
}

const ModifierRow = ({
    title,
    icon,
    amount,
    isAmountHidden,
    parenthetical,
    onEdit,
    onRemove,
    onAdd,
    onUpdateCurrencyAmount,
    deleteButtonMargin,
    isDisabled,
    secondLine,
}: {
    title: string
    icon: IconNameAndCategoryString
    amount: number | null
    isAmountHidden?: boolean
    parenthetical?: string
    onEdit?: () => void
    onRemove?: () => void
    onAdd?: () => void
    onUpdateCurrencyAmount?: (newAmount: number) => void
    deleteButtonMargin?: string
    isDisabled?: boolean
    secondLine?: ReactNode
}) => {
    return (
        <RemovableItemContainer
            className={twm(
                'group flex flex-col items-stretch justify-center gap-y-1',
                secondLine === undefined ? 'h-9' : 'h-16'
            )}
            onRemove={amount !== null && onRemove !== undefined ? onRemove : undefined}
            deleteButtonMargin={deleteButtonMargin}
        >
            <div className="flex items-center justify-between gap-x-4">
                <Icon
                    icon={icon}
                    className={twm([
                        amount !== null
                            ? 'text-gray-500'
                            : isDisabled
                              ? 'text-gray-400'
                              : 'text-primary-700',
                    ])}
                />
                <Label
                    className={twm(
                        'grow font-normal',
                        amount !== null
                            ? 'text-gray-700'
                            : 'text-primary-700 hover:text-primary-700/60 cursor-pointer font-bold underline',
                        isDisabled && 'cursor-default text-gray-400 hover:text-gray-400'
                    )}
                    onClick={amount === null && !isDisabled ? onAdd : undefined}
                >
                    {amount === null ? `Add ${title.toLowerCase()}` : title}
                    {parenthetical !== undefined && ` (${parenthetical})`}
                    {amount !== null && onEdit !== undefined && (
                        <button
                            onClick={onEdit}
                            className="text-primary-700 hover:text-primary-700/60 ml-2 inline-block font-bold underline"
                        >
                            Edit
                        </button>
                    )}
                </Label>

                {onUpdateCurrencyAmount !== undefined ? (
                    <CurrencyField
                        value={amount ?? 0}
                        onChange={(newValue) => onUpdateCurrencyAmount?.(newValue ?? 0)}
                        className="w-[164px] text-right"
                        size="medium"
                    />
                ) : amount !== null && !isAmountHidden ? (
                    <Label className="shrink-0 font-normal">{currencyFormat(amount, true)}</Label>
                ) : null}
            </div>

            {secondLine !== undefined && <div className="pl-10">{secondLine}</div>}
        </RemovableItemContainer>
    )
}
